import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import LogoLoader from '../LogoLoader';

const Card = styled.div`
  padding: 16px 18px;
  background: linear-gradient(135deg, rgba(51, 92, 244, 0.15) 0%, rgba(45, 91, 237, 0.2) 100%);
  border: 1px solid rgba(51, 92, 244, 0.4);
  border-radius: 14px;
  max-width: 360px;
  min-width: 280px;
  margin-right: 10px;
  box-shadow: 0 4px 16px rgba(51, 92, 244, 0.15);
`;
const Header = styled.div`display:flex;align-items:center;gap:8px;font-size:15px;font-weight:600;color:#2c64da;margin-bottom:6px;`;
const Note = styled.div`font-size:13px;color:#455776;line-height:1.4;margin-bottom:12px;`;
const Item = styled.div`display:flex;align-items:center;gap:10px;padding:10px 12px;margin-bottom:8px;background:#f5f8fd;border:1px solid #edf1f9;border-radius:8px;`;
const ItemName = styled.div`flex:1;font-size:13px;color:#33445e;`;
const AttachBtn = styled.button`
  background:#335cf4;color:#fff;border:none;border-radius:6px;padding:6px 12px;font-size:12px;font-weight:600;cursor:pointer;
  display:inline-flex;align-items:center;gap:6px;
  &:disabled{opacity:0.6;cursor:default;}
  &:hover:not(:disabled){background:#2d5bed;}
`;
const Done = styled.span`display:inline-flex;align-items:center;gap:4px;font-size:12px;color:#0f8f63;font-weight:600;`;
const Timestamp = styled.div`font-size:10px;color:#7b8aa3;margin-top:6px;`;

/**
 * Renders an adviser's document request in the client's conversation feed.
 * Each pending item has an Attach button; on file pick it calls onAttach, which
 * uploads + fulfils, and the item flips to a green checkmark.
 */
const DocumentRequestMessage = ({ data, timestamp, onAttach }) => {
  const inputs = useRef({});
  const [state, setState] = useState({}); // requirementKey -> 'busy' | 'done'
  const items = Array.isArray(data?.items) ? data.items : [];

  const pick = (key) => inputs.current[key]?.click();

  const handleFile = async (item, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setState(s => ({ ...s, [item.requirementKey]: 'busy' }));
    try {
      await onAttach(data.requestId, item, file);
      setState(s => ({ ...s, [item.requirementKey]: 'done' }));
    } catch (err) {
      setState(s => ({ ...s, [item.requirementKey]: undefined }));
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error;
      alert(serverMsg || (status ? `Upload failed (HTTP ${status}). Please try again.` : `Upload failed: ${err?.message || 'network error'}. Please try again.`));
    } finally {
      if (e.target) e.target.value = '';
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
      <Card>
        <Header>📎 Documents requested</Header>
        {data?.message ? <Note>{data.message}</Note> : <Note>Your adviser needs the following. Tap Attach to upload each one.</Note>}
        {items.map((item) => {
          const st = state[item.requirementKey];
          const done = st === 'done' || item.status === 'fulfilled';
          return (
            <Item key={item.requirementKey}>
              <ItemName>{item.name}</ItemName>
              {done ? (
                <Done>✓ Attached</Done>
              ) : (
                <>
                  <AttachBtn disabled={st === 'busy'} onClick={() => pick(item.requirementKey)}>
                    {st === 'busy' ? <><LogoLoader compact size={12} onDark label="Uploading" /> Uploading…</> : 'Attach'}
                  </AttachBtn>
                  <input
                    ref={(el) => { inputs.current[item.requirementKey] = el; }}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFile(item, e)}
                  />
                </>
              )}
            </Item>
          );
        })}
        <Timestamp>
          {new Date(timestamp).toLocaleString([], { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </Timestamp>
      </Card>
    </div>
  );
};

export default DocumentRequestMessage;
